import React, { useState } from 'react';
import { Volume2, Piano } from 'lucide-react';
import { playNote } from '../../utils/audioUtils';
type PianoKey = {
  note: string;
  pitch: string;
  isBlack: boolean;
  color?: string;
  chinese?: string;
};
// Full piano octave with black keys
const PIANO_KEYS: PianoKey[] = [{
  note: 'C',
  pitch: '1',
  isBlack: false,
  color: '#FF6B6B',
  chinese: '宫'
}, {
  note: 'C#',
  pitch: '1#',
  isBlack: true
}, {
  note: 'D',
  pitch: '2',
  isBlack: false,
  color: '#FFB347',
  chinese: '商'
}, {
  note: 'D#',
  pitch: '2#',
  isBlack: true
}, {
  note: 'E',
  pitch: '3',
  isBlack: false,
  color: '#FFEB3B',
  chinese: '角'
}, {
  note: 'F',
  pitch: '4',
  isBlack: false,
  color: '#98D8AA'
}, {
  note: 'F#',
  pitch: '4#',
  isBlack: true
}, {
  note: 'G',
  pitch: '5',
  isBlack: false,
  color: '#87CEEB',
  chinese: '徵'
}, {
  note: 'G#',
  pitch: '5#',
  isBlack: true
}, {
  note: 'A',
  pitch: '6',
  isBlack: false,
  color: '#DDA0DD',
  chinese: '羽'
}, {
  note: 'A#',
  pitch: '6#',
  isBlack: true
}, {
  note: 'B',
  pitch: '7',
  isBlack: false,
  color: '#FFB6C1'
}, {
  note: 'C2',
  pitch: "1'",
  isBlack: false,
  color: '#FF6B6B',
  chinese: '宫'
}];
// Pentatonic scale for comparison
const PENTATONIC_NOTES = ['1', '2', '3', '5', '6', "1'"];
export function ScaleExplorer() {
  const [activeKey, setActiveKey] = useState<string | null>(null);
  const [mode, setMode] = useState<'piano' | 'pentatonic'>('piano');
  const [playedNotes, setPlayedNotes] = useState<string[]>([]);
  const handleKeyPress = (key: PianoKey) => {
    setActiveKey(key.pitch);
    playNote(key.pitch);
    setPlayedNotes(prev => [...prev.slice(-7), key.pitch]);
    setTimeout(() => setActiveKey(null), 200);
  };
  const isPentatonic = (pitch: string) => PENTATONIC_NOTES.includes(pitch);
  const whiteKeys = PIANO_KEYS.filter(k => !k.isBlack);
  const blackKeys = PIANO_KEYS.filter(k => k.isBlack);
  // Black key positions (relative to white keys)
  const blackKeyPositions = [0, 1, 3, 4, 5]; // After C, D, F, G, A
  return <div className="flex flex-col items-center w-full max-w-4xl mx-auto" data-id="element-142">
      {/* Mode selector */}
      <div className="flex gap-2 mb-6" data-id="element-143">
        <button onClick={() => setMode('piano')} className={`px-6 py-3 rounded-full font-bold transition-all ${mode === 'piano' ? 'bg-[#4CAF50] text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'}`} data-id="element-144">
          🎹 现代钢琴
        </button>
        <button onClick={() => setMode('pentatonic')} className={`px-6 py-3 rounded-full font-bold transition-all ${mode === 'pentatonic' ? 'bg-[#FF6B6B] text-white shadow-lg' : 'bg-white text-gray-600 hover:bg-gray-100'}`} data-id="element-145">
          🏮 五声调式
        </button>
      </div>

      {/* Piano keyboard */}
      <div className="bg-gradient-to-b from-gray-800 to-gray-900 p-6 rounded-3xl shadow-2xl w-full mb-8" data-id="element-146">
        <div className="relative h-64 md:h-80" data-id="element-147">
          {/* White keys */}
          <div className="flex h-full gap-1" data-id="element-148">
            {whiteKeys.map((key, index) => {
            const isActive = activeKey === key.pitch;
            const showInPentatonic = mode === 'pentatonic' ? isPentatonic(key.pitch) : true;
            return <button key={key.note} onClick={() => handleKeyPress(key)} disabled={mode === 'pentatonic' && !isPentatonic(key.pitch)} className={`flex-1 rounded-b-lg transition-all duration-100 relative ${mode === 'pentatonic' && !isPentatonic(key.pitch) ? 'bg-gray-300 cursor-not-allowed opacity-50' : isActive ? 'bg-gray-100 translate-y-1' : 'bg-white hover:bg-gray-50'} ${showInPentatonic && key.color ? 'border-b-8' : ''}`} style={{
              borderBottomColor: showInPentatonic && key.color ? key.color : 'transparent'
            }} data-id="element-149">
                  {/* Key label */}
                  <div className="absolute bottom-4 left-0 right-0 text-center" data-id="element-150">
                    {key.chinese && showInPentatonic && <div className="text-lg font-bold mb-1" style={{
                  color: key.color
                }} data-id="element-151">
                        {key.chinese}
                      </div>}
                    <div className={`text-sm font-medium ${mode === 'pentatonic' && !isPentatonic(key.pitch) ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-152">
                      {key.pitch.replace("'", "'")}
                    </div>
                    <div className="text-xs text-gray-400" data-id="element-153">{key.note}</div>
                  </div>
                </button>;
          })}
          </div>

          {/* Black keys */}
          <div className="absolute top-0 left-0 right-0 h-[60%] flex pointer-events-none" data-id="element-154">
            {whiteKeys.map((_, index) => {
            if (!blackKeyPositions.includes(index)) return <div key={index} className="flex-1" data-id="element-155" />;
            const blackKeyIndex = blackKeyPositions.indexOf(index);
            const blackKey = blackKeys[blackKeyIndex];
            if (!blackKey) return <div key={index} className="flex-1" data-id="element-156" />;
            const isActive = activeKey === blackKey.pitch;
            const isDisabled = mode === 'pentatonic';
            return <div key={index} className="flex-1 relative" data-id="element-157">
                  <button onClick={() => !isDisabled && handleKeyPress(blackKey)} disabled={isDisabled} className={`absolute right-0 translate-x-1/2 w-[60%] h-full rounded-b-md transition-all duration-100 pointer-events-auto ${isDisabled ? 'bg-gray-500 cursor-not-allowed opacity-30' : isActive ? 'bg-gray-700 translate-y-1' : 'bg-gray-900 hover:bg-gray-800'}`} data-id="element-158" />
                </div>;
          })}
            <div className="flex-1" data-id="element-159" />
          </div>
        </div>
      </div>

      {/* Played notes display */}
      {playedNotes.length > 0 && <div className="bg-white rounded-2xl p-4 mb-6 shadow-lg w-full max-w-md" data-id="element-160">
          <div className="text-sm text-gray-500 mb-2 text-center" data-id="element-161">
            你弹奏的音符：
          </div>
          <div className="flex justify-center gap-2 flex-wrap" data-id="element-162">
            {playedNotes.map((note, i) => {
          const key = PIANO_KEYS.find(k => k.pitch === note);
          return <div key={i} className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-md" style={{
            backgroundColor: key?.color || '#666'
          }} data-id="element-163">
                  {note}
                </div>;
        })}
          </div>
        </div>}

      {/* Info card */}
      <div className={`rounded-xl p-6 max-w-2xl w-full ${mode === 'piano' ? 'bg-green-50 border border-green-100' : 'bg-red-50 border border-red-100'}`} data-id="element-164">
        <div className="flex items-start" data-id="element-165">
          <div className={`p-2 rounded-full mr-4 ${mode === 'piano' ? 'bg-green-100' : 'bg-red-100'}`} data-id="element-166">
            {mode === 'piano' ? <Piano className="w-6 h-6 text-[#4CAF50]" data-id="element-167" /> : <Volume2 className="w-6 h-6 text-[#FF6B6B]" data-id="element-168" />}
          </div>
          <div data-id="element-169">
            <h3 className={`font-bold mb-1 ${mode === 'piano' ? 'text-[#4CAF50]' : 'text-[#FF6B6B]'}`} data-id="element-170">
              {mode === 'piano' ? '现代钢琴键盘' : '中国五声调式'}
            </h3>
            <p className="text-gray-600 text-sm leading-relaxed" data-id="element-171">
              {mode === 'piano' ? '现代钢琴有88个键，这里展示了一个八度（12个音）。白键是自然音，黑键是升降音。试着弹奏不同的音符组合！' : '中国传统音乐使用"宫、商、角、徵、羽"五个音（1, 2, 3, 5, 6）。没有4和7两个音，所以怎么弹都很和谐好听！'}
            </p>
          </div>
        </div>
      </div>
    </div>;
}